import { CameraImageModel } from 'api/models';
import { IconButton } from 'library/buttons';
import { DatePicker } from 'library/datepicker';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'library/icons';
import ImageSliderSkelton from 'modules/dashboard/components/loaders/ImageSliderSkelton';
import { formatDate, formatTime } from 'modules/shared/utils/date-utils';
import moment from 'moment';
import { ForwardedRef, ReactNode, forwardRef, useRef } from 'react';
import { Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ImageSliderCard from './ImageSliderCard';

interface ImageSliderProps {
    images: CameraImageModel[];
    selectedImage: CameraImageModel;
    onSelectedImageChange: (image: CameraImageModel) => void;
    selectedDate: string;
    onSelectedDateChange: (date: string) => void;
    startDate: string;
    endDate: string;
    isLoading: boolean;
    className?: string;
    showDatePicker?: boolean;
    toolbarStart?: ReactNode;
    ref?: ForwardedRef<HTMLDivElement>;
}
const ImageSlider = forwardRef<HTMLDivElement, ImageSliderProps>((props, ref) => {
    const {
        images,
        selectedImage,
        onSelectedImageChange,
        selectedDate,
        onSelectedDateChange,
        startDate,
        endDate,
        isLoading,
        className,
        showDatePicker = true,
        toolbarStart,
    } = props;

    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    return (
        <div ref={ref} className={['flex flex-col gap-4 pt-5 h-[var(--image-slider-height)]', className].join(' ')}>
            <div className="flex justify-between items-center gap-3 px-6  hide-scrollbar">
                {toolbarStart}

                {showDatePicker && (
                    <div className="relative flex items-center gap-2 cursor-pointer font-semibold text-grayscale-900 h-8">
                        <div className="absolute flex items-center gap-2 left-0  pointer-events-none">
                            <IconButton color="secondary" size="xs">
                                <CalendarIcon className="text-grayscale-600" width="16" height="18" />
                            </IconButton>
                        </div>
                        <DatePicker
                            className="bg-transparent outline-none pl-11 w-full"
                            value={moment(selectedDate, 'YYYYMMDD').toDate()}
                            onChange={(date) => onSelectedDateChange(moment(date).format('YYYYMMDD'))}
                            maxDate={moment(endDate, 'YYYYMMDD').toDate()}
                            minDate={moment(startDate, 'YYYYMMDD').toDate()}
                            format="DD MMM, YYYY"
                        />
                    </div>
                )}
                <div className="ml-auto flex items-center gap-3">
                    <button
                        ref={navigationPrevRef}
                        className="flex justify-center items-center w-6 h-6 rounded-md bg-card-color btn-secondary-shadow btn-secondary-border btn-secondary-hover"
                    >
                        <ChevronLeftIcon className="text-grayscale-600" width="6" height="10" />
                    </button>
                    <button
                        ref={navigationNextRef}
                        className="flex justify-center items-center w-6 h-6 rounded-md bg-card-color btn-secondary-shadow btn-secondary-border btn-secondary-hover"
                    >
                        <ChevronRightIcon className="text-grayscale-600" width="6" height="10" />
                    </button>
                </div>
            </div>

            <div className="px-6 overflow-hidden">
                {isLoading ? (
                    <ImageSliderSkelton count={16} />
                ) : images.length > 0 ? (
                    <Swiper
                        dir="rtl"
                        spaceBetween={20}
                        slidesPerView={'auto'}
                        className="overflow-visible"
                        modules={[Navigation, Keyboard]}
                        keyboard={{
                            enabled: true,
                        }}
                        navigation={{
                            // Because we are reversing the direction, next becomes prev & prev becomes next
                            prevEl: navigationNextRef.current,
                            nextEl: navigationPrevRef.current,
                        }}
                    >
                        {images.map((img, i) => (
                            <SwiperSlide key={img._id} className="!w-auto" dir="ltr">
                                <ImageSliderCard
                                    url={img.urlThumb}
                                    time={formatTime(img.time, 'h:mm a')}
                                    isActive={img._id === selectedImage._id}
                                    onClick={() => onSelectedImageChange(img)}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                ) : (
                    <div className="text-center font-medium mt-6">
                        No images on {formatDate(selectedDate, 'DD MMM, YYYY')}
                    </div>
                )}
            </div>
        </div>
    );
});

export default ImageSlider;
